import React, { useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  autoUpdate,
  flip,
  FloatingOverlay,
  useClick,
  useClientPoint,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { useAppDispatch } from '../../../redux/hooks';
import { toggleCommentMode } from '../../../redux/slices/conversationThreads';
import useStartConversationThread from '../hooks/useStartConversationThread';
import {
  ConversationThreadEntityType,
  ICommentInput,
} from '../../../utils/apollo/graphqlSchema';
import { theme } from '../../../styles/theme';
import { FloatingLayer } from '../../FloatingManager';
import StartThreadPopover from './StartThreadPopover';
import { ICommentInputInfo } from '../types';

interface IProps {
  entityId: string;
  entityType: ConversationThreadEntityType;
  zIndex: number;
  onSubmit: (commentInputInfo: ICommentInputInfo) => void;
}

// Renders a full width and full height backdrop with a custom cursor
const StyledBackdrop = styled.div<{
  $isActive: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  cursor: url('/images/chat-teardrop-fill.svg'), auto;
  ${({ $isActive }) =>
    $isActive
      ? css`
          &:hover {
            box-shadow: 0 0 2px 2px ${theme.colors.mainBlue};
          }
        `
      : css`
          box-shadow: 0 0 2px 2px ${theme.colors.mainBlue};
        `};
`;

const NewConversationOverlay = ({
  entityId,
  entityType,
  zIndex,
  onSubmit,
}: IProps) => {
  const [showPopover, setShowPopover] = useState(false);

  const dispatch = useAppDispatch();
  const startThread = useStartConversationThread();

  const { refs, floatingStyles, context } = useFloating({
    open: showPopover,
    onOpenChange: (open) => {
      setShowPopover(open);
      if (!open) {
        dispatch(toggleCommentMode());
      }
    },
    whileElementsMounted: autoUpdate,
    middleware: [flip()],
    placement: 'bottom-start',
  });

  const click = useClick(context);
  const clientPoint = useClientPoint(context, {
    enabled: !showPopover,
  });
  const dismiss = useDismiss(context);
  const { getFloatingProps, getReferenceProps } = useInteractions([
    click,
    clientPoint,
    dismiss,
  ]);

  const deactivateCommenting = useCallback(() => {
    setShowPopover(false);
    dispatch(toggleCommentMode());
  }, [dispatch]);

  const handleStartThread = useCallback(
    async (
      commentInput: ICommentInput,
      commentInputInfo: ICommentInputInfo,
    ) => {
      await startThread({
        entity: {
          id: entityId,
          type: entityType,
        },
        comment: commentInput,
      });

      deactivateCommenting();

      onSubmit(commentInputInfo);
    },
    [deactivateCommenting, entityId, entityType, onSubmit, startThread],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        deactivateCommenting();
      }
    },
    [deactivateCommenting],
  );

  useEffect(() => {
    // Listen for keydown event to cancel on Escape
    document.addEventListener('keydown', handleKeyDown, false);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
    };
  }, [handleKeyDown]);

  return (
    <>
      <StyledBackdrop
        ref={refs.setReference}
        style={{ zIndex }}
        $isActive={!showPopover}
        {...getReferenceProps()}
      />
      {showPopover && (
        <FloatingLayer>
          <FloatingOverlay lockScroll={true}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <StartThreadPopover onSubmit={handleStartThread} />
            </div>
          </FloatingOverlay>
        </FloatingLayer>
      )}
    </>
  );
};

export default NewConversationOverlay;
